import React, { createContext, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Lets a page render its own controls **into the SideNav title row**, on the same line as the
 * page title, without lifting that page's state anywhere.
 *
 * Why a portal instead of `RenderTabsByRoute`: that mechanism mounts a component by route inside
 * the top bar, so whatever it mounts has to be self-contained — in practice, backed by a Redux
 * slice (see `TagsManagerFilters`). That works for a filter bar whose state already lives in the
 * store, but not for a toolbar whose values are derived from data fetched by the page itself.
 * This slot inverts the direction: the header offers a destination, the page keeps ownership of
 * its state and renders through it.
 *
 * Other routes are unaffected — the target is an empty flex child until something fills it.
 */
const SideNavTitleSlotContext = createContext<{
  node: HTMLDivElement | null;
  setNode: (node: HTMLDivElement | null) => void;
} | null>(null);

/**
 * Routes allowed to fill the title slot.
 *
 * SideNav is shared by every screen in the app, so this is an explicit allowlist rather than an
 * always-on target: on any route not listed here the title row renders exactly the markup it did
 * before the slot existed. A page cannot affect the top bar of a screen that did not opt in.
 */
const TITLE_SLOT_ROUTES = ["/bgp-visibility"];

export const routeUsesTitleSlot = (pathname: string) =>
  TITLE_SLOT_ROUTES.some((route) => pathname.includes(route));

export const SideNavTitleSlotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const value = useMemo(() => ({ node, setNode }), [node]);

  return (
    <SideNavTitleSlotContext.Provider value={value}>{children}</SideNavTitleSlotContext.Provider>
  );
};

/** Rendered by SideNav — the destination sitting beside the page title. */
export const SideNavTitleSlotTarget: React.FC<{ className?: string }> = ({ className }) => {
  const context = useContext(SideNavTitleSlotContext);

  return <div ref={context?.setNode} className={className} />;
};

/**
 * Rendered by a page — sends `children` up into the title row.
 *
 * Falls back to rendering in place when there is no slot (a page mounted outside `AppLayout`, or
 * before the target has attached), so the controls can never silently vanish.
 */
export const SideNavTitlePortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const context = useContext(SideNavTitleSlotContext);

  if (!context?.node) return <>{children}</>;
  return createPortal(children, context.node);
};
